// quick-260815-dhs: mobile-only sticky anchor-chip nav for the public store page.
// Sits directly below the 54px sticky site header (z-40 < header's z-50) and
// smooth-scrolls to page sections. Rendered via CSS breakpoint gating (lg:hidden),
// never useIsMobile — the markup is identical on server and client (SSR-safe).
// quick-260815-mkf: scroll-spy via IntersectionObserver inside useEffect (SSR-safe
// because effects never run on the server) — the chip whose section occupies the
// upper viewport is filled primary; taps flip the active chip immediately.

import { useEffect, useState } from "react";

type SectionChip = {
  label: string;
  targetId: string;
};

type StoreSectionNavProps = {
  hasSimilarStores?: boolean;
};

export default function StoreSectionNav({
  hasSimilarStores = false,
}: StoreSectionNavProps) {
  const [activeId, setActiveId] = useState("overview");

  const chips: SectionChip[] = [
    { label: "نظرة عامة", targetId: "overview" },
    { label: "التقييمات", targetId: "reviews" },
    ...(hasSimilarStores
      ? [{ label: "متاجر مشابهة", targetId: "similar-stores" }]
      : []),
  ];

  useEffect(() => {
    const targetIds = [
      "overview",
      "reviews",
      ...(hasSimilarStores ? ["similar-stores"] : []),
    ];

    // Conditional sections (#similar-stores) or fallback wrappers may be
    // absent — skip nulls, never throw.
    const targets = targetIds
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => el !== null);

    if (targets.length === 0) return;

    // id → currently intersecting. Active chip = FIRST id in document order
    // that intersects ("topmost intersecting section").
    const intersecting = new Map<string, boolean>();

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          intersecting.set(entry.target.id, entry.isIntersecting);
        }
        const next = targetIds.find((id) => intersecting.get(id));
        if (next) setActiveId(next);
      },
      // -110px top compensates for the two sticky bars (54px site header +
      // ~46px chip bar); -55% bottom marks a section active once it occupies
      // the upper part of the viewport.
      { rootMargin: "-110px 0px -55% 0px", threshold: 0 }
    );

    targets.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [hasSimilarStores]);

  return (
    <nav
      dir="rtl"
      aria-label="أقسام الصفحة"
      className="lg:hidden sticky top-[54px] z-40"
      style={{
        background: "var(--ys-paper)",
        borderBottom: "1px solid var(--ys-rule)",
      }}
    >
      <div className="flex flex-nowrap gap-2 overflow-x-auto scrollbar-hide px-4 py-2">
        {chips.map((chip) => {
          const isActive = activeId === chip.targetId;
          return (
            <button
              key={chip.targetId}
              type="button"
              aria-current={isActive ? "true" : undefined}
              onClick={() => {
                // Flip active state immediately on tap — don't wait for IO
                // callbacks during the smooth scroll.
                setActiveId(chip.targetId);
                // Event-handler-scoped document access — SSR-safe.
                document
                  .getElementById(chip.targetId)
                  ?.scrollIntoView({ behavior: "smooth" });
              }}
              className="shrink-0"
              style={{
                fontFamily: "var(--ys-font)",
                fontSize: 12.5,
                fontWeight: isActive ? 600 : 500,
                color: isActive ? "#fff" : "var(--ys-ink-2)",
                background: isActive ? "var(--ys-primary)" : "transparent",
                border: isActive
                  ? "1px solid var(--ys-primary)"
                  : "1px solid var(--ys-rule)",
                borderRadius: "var(--ys-r-pill)",
                padding: "5px 14px",
                whiteSpace: "nowrap",
                cursor: "pointer",
                transition:
                  "background 150ms var(--ys-ease), color 150ms var(--ys-ease), border-color 150ms var(--ys-ease)",
              }}
            >
              {chip.label}
            </button>
          );
        })}
      </div>
    </nav>
  );
}
